import { faStore } from '@fortawesome/pro-solid-svg-icons'

import { apiAxios } from '@/api'
import { fetchWrapper } from '@/reducers/fetch'
import type { Dispatch } from '@/utils/typeHelpers'

import {
  COMPLETE_SEPARATION_KEY,
  FETCH_SEPARATION_QUESTIONS_KEY,
  MIX_VALUE,
  ROW_OPTIONS,
  SEPARATION_API_PATH,
  SeparationAnswerValue,
  SINGLE_TOGGLE_OPTIONS,
  SKIP_SEPARATION_KEY,
  SUBMIT_SEPARATION_ANSWERS_KEY,
  type SeparationSkipScreen,
  TOGGLE_OPTIONS,
} from './separation.constants'
import {
  setSeparationLoadFailed,
  setSeparationQuestionSet,
} from './separation.slice'
import type {
  CompleteSeparationResult,
  SeparationEventKind,
  SeparationAnswerPayload,
  SeparationAnswerResult,
  SeparationQuestion,
  SeparationQuestionsResponse,
} from './separation.types'
import { transactionIdsOf } from './transactionIds'

/**
 * The API owns the data; the toggle sets and header icon stay client-side presentation.
 * `suggested` becomes '' rather than null when the prior abstains, since the screen treats
 * an empty string as "no recommendation" and renders no badge.
 */
const toQuestion = (
  question: SeparationQuestionsResponse['questions'][number]
): SeparationQuestion => {
  const mapped: SeparationQuestion = {
    id: question.id,
    kind: question.kind,
    headerName: question.headerName,
    headerSubtext: question.headerSubtext,
    headerIcon: question.logoUrl
      ? { src: question.logoUrl }
      : { icon: faStore },
    amount: question.amount,
    questionText: question.questionText,
    // Money movement supplies its own wording server-side: its answers are equity and
    // transfer categories, not business/personal.
    toggleOptions: question.options ?? TOGGLE_OPTIONS,
    suggested: question.suggested ?? '',
    expandValue: MIX_VALUE,
    bannerText: question.bannerText ?? '',
    subItemLayout: question.subItemLayout,
    subItems: question.subItems,
    subItemToggleOptions: question.rowOptions ?? ROW_OPTIONS,
  }
  if (transactionIdsOf(mapped).length !== 1) return mapped

  return {
    ...mapped,
    toggleOptions: (question.options ?? SINGLE_TOGGLE_OPTIONS).filter(
      (option) => option.value !== MIX_VALUE
    ),
    unsureValue: SeparationAnswerValue.Unsure,
  }
}

export const fetchSeparationQuestions = () =>
  fetchWrapper({
    fetchKey: FETCH_SEPARATION_QUESTIONS_KEY,
    defaultErrorMessage: 'Cannot load transactions to sort',
    // Marks the failure, which is what lets the screens show an end state and the poll
    // stop. `status` is untouched by a failed request, so it cannot carry this on its own.
    handleError: (dispatch: Dispatch) => dispatch(setSeparationLoadFailed()),
    fetchFunction: async (dispatch: Dispatch) => {
      const { data } = await apiAxios.get<SeparationQuestionsResponse>(
        `${SEPARATION_API_PATH}/questions`
      )
      const questions = data.questions.map(toQuestion)
      dispatch(
        setSeparationQuestionSet({
          status: data.status,
          sortedCount: data.sortedCount,
          counts: { ...data.counts, unresolved: data.unresolvedCount },
          deferred: data.deferred,
          questions,
        })
      )
      return { questions, maxQuestions: data.maxQuestions }
    },
  })

const NOTHING_TO_SAVE: SeparationAnswerResult = {
  businessCount: 0,
  personalCount: 0,
  unsureCount: 0,
  movementCount: 0,
}

/** Resolves to `undefined` on failure, which is what the screen reads to stay put. */
export const submitSeparationAnswers = (
  answers: SeparationAnswerPayload[],
  askedHaven?: boolean
) =>
  fetchWrapper({
    fetchKey: SUBMIT_SEPARATION_ANSWERS_KEY,
    defaultErrorMessage: 'Cannot save your answers',
    fetchFunction: async () => {
      if (answers.length === 0) return NOTHING_TO_SAVE
      const { data } = await apiAxios.post<SeparationAnswerResult>(
        `${SEPARATION_API_PATH}/answers`,
        { answers, askedHaven }
      )
      return data
    },
  })

/**
 * Signals that the user has finished sorting, which is what releases their answers to
 * autocat. Called after the last autosave settles so nothing is queued mid-correction.
 */
export const completeSeparation = () =>
  fetchWrapper({
    fetchKey: COMPLETE_SEPARATION_KEY,
    defaultErrorMessage: 'Cannot finish sorting',
    fetchFunction: async () => {
      const { data } = await apiAxios.post<CompleteSeparationResult>(
        `${SEPARATION_API_PATH}/complete`
      )
      return data
    },
  })

/** Fire-and-forget: a failed log must never surface to the user or hold up the flow. */
export const postSeparationEvent = (kind: SeparationEventKind) => {
  void apiAxios
    .post<undefined>(`${SEPARATION_API_PATH}/events`, { kind })
    .catch(() => undefined)
}

/** Leaves sorting early. Answers already saved stand, and the rest goes to autocat. */
export const skipSeparation = (body: {
  screen: SeparationSkipScreen
  openQuestions: number
}) =>
  fetchWrapper({
    fetchKey: SKIP_SEPARATION_KEY,
    defaultErrorMessage: 'Cannot skip sorting',
    fetchFunction: async () => {
      const { data } = await apiAxios.post<CompleteSeparationResult>(
        `${SEPARATION_API_PATH}/skip`,
        body
      )
      return data
    },
  })
